import { useEffect, useState, useMemo } from "react";
import { api } from "@/api/client";
import { PageHeader, fmtDate } from "@/components/common";
import { Loader2, Search, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

const MODULES = ["auth","users","roles","task","approval_workflow","whatsapp","memory","settings","department","recurring"];

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({});

  // filters
  const [moduleFilter, setModuleFilter] = useState("");
  const [actionSearch, setActionSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  // column sort (client-side on current page)
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 100 };
      if (moduleFilter) params.module = moduleFilter;
      if (actionSearch.trim()) params.action = actionSearch.trim();
      const r = await api.get("/audit-logs", { params });
      setRows(r.data?.data || []);
      setMeta(r.data?.meta || {});
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, moduleFilter, actionSearch]); // eslint-disable-line

  // reset page when filters change
  useEffect(() => { setPage(1); }, [moduleFilter, actionSearch]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  // client-side user search + sort on current page
  const displayed = useMemo(() => {
    let data = rows;
    if (userSearch.trim()) {
      const q = userSearch.toLowerCase();
      data = data.filter(r => (r.user_name || "").toLowerCase().includes(q));
    }
    if (sortKey) {
      data = [...data].sort((a, b) => {
        const va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
        const cmp = String(va).localeCompare(String(vb));
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return data;
  }, [rows, userSearch, sortKey, sortDir]);

  const SortTh = ({ label, k }) => (
    <th onClick={() => toggleSort(k)} className="cursor-pointer select-none group">
      <div className="flex items-center gap-1.5">{label}
        {sortKey === k ? (sortDir === "asc" ? <ChevronUp size={11} className="text-[#334E7B]"/> : <ChevronDown size={11} className="text-[#334E7B]"/>)
          : <ChevronsUpDown size={11} className="opacity-0 group-hover:opacity-50 transition-opacity"/>}
      </div>
    </th>
  );

  const hasFilters = moduleFilter || actionSearch || userSearch;

  return (
    <div data-testid="audit-page">
      <PageHeader title="Audit Logs" breadcrumbs={["Reports", "Audit"]}/>

      {/* Filters */}
      <div className="erp-card p-3 mb-4 flex flex-wrap items-center gap-2">
        {/* Module */}
        <select className="erp-input max-w-[180px] text-sm" value={moduleFilter}
          onChange={e => setModuleFilter(e.target.value)}>
          <option value="">All Modules</option>
          {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        {/* Action search */}
        <div className="relative min-w-[160px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={actionSearch} onChange={e => setActionSearch(e.target.value)}
            placeholder="Filter action..." className="erp-input pl-8 text-sm" />
          {actionSearch && <button onClick={() => setActionSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12}/></button>}
        </div>

        {/* User search (client-side) */}
        <div className="relative min-w-[160px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
            placeholder="Filter user..." className="erp-input pl-8 text-sm" />
          {userSearch && <button onClick={() => setUserSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12}/></button>}
        </div>

        {hasFilters && (
          <button onClick={() => { setModuleFilter(""); setActionSearch(""); setUserSearch(""); }}
            className="text-xs text-slate-500 hover:text-slate-800 underline">Clear all</button>
        )}

        <span className="text-xs text-slate-500 ml-auto whitespace-nowrap">
          {userSearch ? `${displayed.length} of ${rows.length}` : `${meta?.total ?? rows.length} entries`}
        </span>
      </div>

      <div className="erp-card overflow-x-auto">
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline"/></div> : (
          <table className="erp-table">
            <thead>
              <tr>
                <SortTh label="Time" k="created_at"/>
                <SortTh label="Module" k="module"/>
                <SortTh label="Action" k="action"/>
                <SortTh label="User" k="user_name"/>
                <SortTh label="Source" k="source"/>
                <th>Record</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(r => (
                <tr key={r.id}>
                  <td className="text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td><span className="badge-base bg-slate-50 text-slate-700 border-slate-200">{r.module}</span></td>
                  <td className="font-mono text-xs">{r.action}</td>
                  <td>{r.user_name || "—"}</td>
                  <td><span className="badge-base bg-blue-50 text-blue-700 border-blue-200">{r.source || "—"}</span></td>
                  <td className="font-mono text-[10px] text-slate-500 truncate max-w-[160px]">{r.record_id || "—"}</td>
                  <td className="text-xs text-slate-600">{r.remarks || "—"}</td>
                </tr>
              ))}
              {displayed.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-sm text-slate-400">No matching entries</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {meta?.total_pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="erp-btn-secondary">Prev</button>
          <span className="text-slate-600">Page {page} / {meta.total_pages}</span>
          <button disabled={page >= meta.total_pages} onClick={() => setPage(p => p + 1)} className="erp-btn-secondary">Next</button>
        </div>
      )}
    </div>
  );
}
